/**
 * @file 文章列表頁面
 * @description 顯示所有文章列表並支援分頁
 */
import { useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import routerPath from 'routes/router.path';
import api from 'services/api';

import './postsListStyle.scss';

import ExceptionHandleService from 'utils/exceptionHandler';
import { GetPostsClass, Post } from './getPostsClass';
import type { MetaData } from './getPostsClass';

const _EHS = new ExceptionHandleService({
  _NAME: 'pages/frontStage/posts/postList/PostList.tsx',
  _NOTICE: ''
});

/**
 * 文章列表元件
 */
function PostList(): ReactNode {
  /** 文章列表 */
  const [listData, setListData] = useState<Post[]>([]);
  /** 分頁資料 */
  const [pageMeta, setPageMeta] = useState<MetaData>({
    page: 1,
    perPage: 10,
    totalCount: 0,
    totalPages: 0
  });

  const navigate = useNavigate();

  /** 取得文章列表 */
  const getPostList = useCallback(
    async (page: number = 1, perPage: number = 10): Promise<GetPostsClass | undefined> => {
      try {
        const apiReq = {
          perPage: perPage,
          page: page
        };
        const res = await api.posts.getPostList(apiReq);
        const apiRes = res;
        if (apiRes) {
          const res = new GetPostsClass(apiRes);
          return res;
        }
      } catch (error) {
        _EHS.errorReport(error, 'getPostList', _EHS._LEVEL.ERROR);
      }
    },
    []
  );

  /** 初次載入 */
  const getInit = useCallback(async () => {
    try {
      const postList = await getPostList();
      const metaData = postList.metaData;
      const posts = postList.postList;
      setPageMeta(metaData);
      setListData(posts);
    } catch (error) {
      _EHS.errorReport(error, 'getInit', _EHS._LEVEL.ERROR);
    }
  }, [getPostList]);

  /** 初始化 */
  useEffect(() => {
    getInit();
  }, [getInit]);

  /** 前往文章詳情頁 */
  const handlePostDetail = (id: string): void => {
    navigate(`/${routerPath.posts}/${routerPath.postPage}?id=${id}`, {});
  };

  /** 上下頁切換 */
  const handlePageFetch = async (page: number): Promise<void> => {
    const postList = await getPostList(page, pageMeta.perPage);
    const metaData = postList.metaData;
    const posts = postList.postList;
    setPageMeta(metaData);
    setListData(posts);

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth' // 使用平滑滾動效果
    });
  };

  return (
    <div id='posts' className='posts_container'>
      {/* 列表 */}
      <div className='posts_list'>
        {listData?.length > 0 &&
          listData.map((post, index) => (
            <div
              key={`post_id_${index}`}
              className='posts_item'
              onClick={() => handlePostDetail(post.id)}
            >
              <h2 className='posts_item_title'>{post.title}</h2>
              <time className='posts_item_date'>{post.date}</time>
              <span className='posts_item_category'>{post.category}</span>
              <div className='posts_item_content'>{post.content}</div>
            </div>
          ))}
      </div>
      {/* 分頁 */}
      <div className='posts_pagination'>
        <div className='posts_pagination_info'>
          共 {pageMeta.totalCount} 筆，共 {pageMeta.totalPages} 頁
        </div>
        <div className='posts_pagination_page'>
          <button
            onClick={() => handlePageFetch(pageMeta.page > 1 ? pageMeta.page - 1 : 1)}
            disabled={pageMeta.page === 1}
          >
            上一頁
          </button>
          <button
            onClick={() =>
              handlePageFetch(
                pageMeta.page < pageMeta.totalPages && pageMeta.totalPages > 1
                  ? pageMeta.page + 1
                  : 1
              )
            }
            disabled={pageMeta.page === pageMeta.totalPages}
          >
            下一頁
          </button>
        </div>
      </div>
    </div>
  );
}

export default PostList;
